import { logger } from "@/utils/logger";
import React from 'react';
import { Button } from '@/components/ui/button';
import { MapPin, Navigation, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useDealerSession } from '@/contexts/DealerSessionContext';
import { useLanguage } from '@/contexts/LanguageContext';

interface GoogleMapsLinkMessageProps {
  content: {
    text: string;
    mapsUrl: string;
    dealerName?: string | null;
    dealerAddress?: string | null;
  };
}

const GoogleMapsLinkMessage: React.FC<GoogleMapsLinkMessageProps> = ({ content }) => {
  const { sessionData } = useDealerSession();
  const { t } = useLanguage();
  
  const handleOpenMaps = () => {
    window.open(content.mapsUrl, '_blank', 'noopener,noreferrer');
  };

  // Extract custom dealer name from double brackets [[Name]]
  const extractCustomDealerName = (text: string): string | null => {
    const match = text.match(/\[\[(.+?)\]\]/);
    return match ? match[1].trim() : null;
  };

  // Extract custom address from triple brackets [[[Address]]]
  const extractCustomAddress = (text: string): string | null => {
    const match = text.match(/\[\[\[(.+?)\]\]\]/);
    return match ? match[1].trim() : null;
  };

  // Extract destination from URL if available
  const extractDestination = (url: string): { street: string; cityProvincePostal: string } | null => {
    try {
      const urlObj = new URL(url);
      // Check for destination parameters (directions API)
      const destination = urlObj.searchParams.get('destination');
      const daddr = urlObj.searchParams.get('daddr');
      const query = urlObj.searchParams.get('q');
      const rawAddress = destination || daddr || query;
      const addressString = rawAddress ? decodeURIComponent(rawAddress) : null;
      
      if (addressString) {
        // Split address by comma
        const parts = addressString.split(',').map(part => part.trim());
        
        if (parts.length >= 3) {
          // Format: "Street, City, Province Postal"
          const street = parts[0];
          const cityProvincePostal = parts.slice(1).join(', ');
          return { street, cityProvincePostal };
        } else if (parts.length === 2) {
          // Format: "Street, City Province Postal"
          return { street: parts[0], cityProvincePostal: parts[1] };
        }
      }
    } catch (error) {
      logger.warn('Failed to extract destination from URL:', error);
    }
    return null;
  };

  const customDealerName = content.dealerName ?? (content.text ? extractCustomDealerName(content.text) : null);
  const customAddress = content.dealerAddress ?? (content.text ? extractCustomAddress(content.text) : null);
  const destination = extractDestination(content.mapsUrl);
  
  // Strip bracket markers from display text: [[dealer]] and [[[address]]]
  const cleanDisplayText = content.text
    ? content.text.replace(/\[\[\[[^\]]+\]\]\]/g, '').replace(/\[\[[^\]]+\]\]/g, '').trim()
    : null;
  
  const displayDealerName = customDealerName || sessionData?.dealerName;
  const displayAddress =
    customAddress || (destination ? `${destination.street}, ${destination.cityProvincePostal}` : null);

  return (
    <div className="w-full space-y-2">
      {cleanDisplayText && (
        <div className="bg-gray-100 text-gray-800 rounded-lg rounded-bl-sm px-4 py-2">
          <p className="whitespace-pre-wrap break-words">{cleanDisplayText}</p>
        </div>
      )}
      
      <Card className="w-full bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 shadow-sm hover:shadow-md transition-shadow">
        <CardContent className="w-full p-4">
          <div className="flex items-start gap-3">
            <div className="bg-blue-500 rounded-full p-2 flex-shrink-0">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Navigation className="w-4 h-4 text-blue-600" />
                <div className="font-semibold text-gray-900 text-sm">{t('chat.maps.get.directions')}</div>
              </div>
              
              {displayDealerName && (
                <p className="font-semibold text-gray-900 text-base mb-1">{displayDealerName}</p>
              )}
              
              {displayAddress ? (
                <div className="text-sm text-gray-700 mb-3">
                  <p className="break-words">{displayAddress}</p>
                </div>
              ) : (
                <p className="text-sm text-gray-700 mb-3">{t('chat.maps.address.unavailable')}</p>
              )}
              
              <Button
                onClick={handleOpenMaps}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                {t('chat.maps.open')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GoogleMapsLinkMessage;
